/** The payoff graph with Sensibull-style interaction (operator request
 * 2026-07-27): round-level axis ticks, and a hover crosshair whose
 * tooltip shows the exact P&L at the hovered underlying level — today's
 * curve interpolated, on-expiry computed EXACTLY from the legs (kinks at
 * strikes must not be smoothed). Split out of PayoffCard.tsx for the
 * 400-line lint. */

import { useRef, useState, type MouseEvent, type ReactElement } from "react";
import { cn } from "@/lib/cn";
import { ZoomButtons } from "@/components/ui/ZoomButtons";
import type { PayoffView } from "@/lib/payoffApi";
import {
  PAYOFF_H,
  PAYOFF_W,
  areaPath,
  expiryPnlAt,
  formatLevel,
  formatMoney,
  linePath,
  pnlAt,
  pnlBounds,
  pnlTone,
  tableTargets,
  xAt,
  yAt,
  type PayoffBounds,
} from "./payoffMath";

export function PayoffChart({ data }: Readonly<{ data: PayoffView }>): ReactElement {
  const [zoom, setZoom] = useState(1);
  const view = zoomView(data, zoom);
  const series = view.todayPnl === null ? [view.expiryPnl] : [view.expiryPnl, view.todayPnl];
  const bounds = pnlBounds(series);
  const wrap = useRef<HTMLDivElement>(null);
  const [frac, setFrac] = useState<number | null>(null);
  const onMove = (e: MouseEvent<HTMLDivElement>): void => {
    const rect = wrap.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0) return;
    setFrac(Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1));
  };
  const first = view.spots[0] ?? 0;
  const last = view.spots[view.spots.length - 1] ?? first;
  const hoverSpot = frac === null ? null : first + frac * (last - first);
  return (
    <div>
      <div className="mb-1 flex justify-end">
        <ZoomButtons zoom={zoom} max={4} onChange={setZoom} />
      </div>
      <div
        ref={wrap}
        className="relative"
        data-testid="payoff-hover-area"
        onMouseMove={onMove}
        onMouseLeave={() => {
          setFrac(null);
        }}
      >
        <ChartSvg data={view} bounds={bounds} hoverSpot={hoverSpot} />
        {hoverSpot !== null && frac !== null && (
          <HoverTip data={view} spot={hoverSpot} frac={frac} />
        )}
      </div>
      <TickLabels spots={view.spots} />
      <ChartFooter data={view} />
    </div>
  );
}

/** Zoomed window of the payoff grid, CENTERED on spot (falling back to
 * the grid middle) — a payoff zooms around where the market is, not
 * toward one tail. On-expiry P&L in the tooltip stays exact either way
 * (computed from the legs, not the sampled grid). */
function zoomView(data: PayoffView, zoom: number): PayoffView {
  const n = data.spots.length;
  if (zoom <= 1 || n < 5) return data;
  const keep = Math.max(Math.ceil(n / zoom), 3);
  const centerValue = data.spot ?? data.spots[Math.floor(n / 2)] ?? 0;
  let center = 0;
  for (let i = 1; i < n; i += 1) {
    const spotI = data.spots[i] ?? 0;
    const spotC = data.spots[center] ?? 0;
    if (Math.abs(spotI - centerValue) < Math.abs(spotC - centerValue)) center = i;
  }
  const start = Math.min(Math.max(center - Math.floor(keep / 2), 0), n - keep);
  return {
    ...data,
    spots: data.spots.slice(start, start + keep),
    expiryPnl: data.expiryPnl.slice(start, start + keep),
    todayPnl: data.todayPnl === null ? null : data.todayPnl.slice(start, start + keep),
  };
}

function ChartSvg({
  data,
  bounds,
  hoverSpot,
}: Readonly<{ data: PayoffView; bounds: PayoffBounds; hoverSpot: number | null }>): ReactElement {
  return (
    <svg
      viewBox={`0 0 ${String(PAYOFF_W)} ${String(PAYOFF_H)}`}
      preserveAspectRatio="none"
      className="h-52 w-full"
      role="img"
      aria-label="Basket payoff vs underlying level"
    >
      <ExpiryAreaFills data={data} bounds={bounds} />
      <TickGrid spots={data.spots} />
      <SpotMarker data={data} />
      {data.todayPnl !== null && (
        <path
          d={linePath(data.spots, data.todayPnl, bounds)}
          fill="none"
          stroke="var(--accent, #2563eb)"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      )}
      <path
        d={linePath(data.spots, data.expiryPnl, bounds)}
        fill="none"
        stroke="var(--fill-success, #16a34a)"
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {hoverSpot !== null && <Crosshair data={data} spot={hoverSpot} bounds={bounds} />}
    </svg>
  );
}

/** Zero axis + the expiry line's shaded regions: one closed area path
 * filled twice, clipped above zero (green) and below (red). */
function ExpiryAreaFills({
  data,
  bounds,
}: Readonly<{ data: PayoffView; bounds: PayoffBounds }>): ReactElement {
  const zeroY = yAt(0, bounds);
  const area = areaPath(data.spots, data.expiryPnl, bounds);
  return (
    <>
      <clipPath id="payoff-above">
        <rect x={0} y={0} width={PAYOFF_W} height={Math.max(zeroY, 0)} />
      </clipPath>
      <clipPath id="payoff-below">
        <rect x={0} y={zeroY} width={PAYOFF_W} height={Math.max(PAYOFF_H - zeroY, 0)} />
      </clipPath>
      <path d={area} fill="var(--fill-success)" opacity={0.14} clipPath="url(#payoff-above)" />
      <path d={area} fill="var(--fill-danger)" opacity={0.14} clipPath="url(#payoff-below)" />
      <line
        x1={0}
        y1={zeroY}
        x2={PAYOFF_W}
        y2={zeroY}
        stroke="var(--line, #e5e7eb)"
        strokeWidth={1}
      />
    </>
  );
}

/** Faint vertical gridline per round level — the tick marks of the
 * labels row below. */
function TickGrid({ spots }: Readonly<{ spots: readonly number[] }>): ReactElement {
  return (
    <>
      {tableTargets(spots).map((t) => (
        <line
          key={t}
          x1={xAt(t, spots)}
          y1={0}
          x2={xAt(t, spots)}
          y2={PAYOFF_H}
          stroke="var(--line, #e5e7eb)"
          strokeWidth={0.5}
          opacity={0.6}
        />
      ))}
    </>
  );
}

/** Round-level labels under the chart, positioned to line up with their
 * gridlines (HTML, not SVG text — preserveAspectRatio=none would
 * stretch glyphs). */
function TickLabels({ spots }: Readonly<{ spots: readonly number[] }>): ReactElement {
  return (
    <div className="relative h-4 overflow-hidden font-mono text-[10px] text-ink-muted">
      {tableTargets(spots).map((t) => (
        <span
          key={t}
          className="absolute -translate-x-1/2"
          style={{ left: `${String((xAt(t, spots) / PAYOFF_W) * 100)}%` }}
        >
          {formatLevel(t)}
        </span>
      ))}
    </div>
  );
}

function SpotMarker({ data }: Readonly<{ data: PayoffView }>): ReactElement | null {
  if (data.spot === null) return null;
  const x = xAt(data.spot, data.spots);
  return (
    <line
      x1={x}
      y1={0}
      x2={x}
      y2={PAYOFF_H}
      stroke="var(--ink-secondary, #6b7280)"
      strokeWidth={1}
      strokeDasharray="4 3"
    />
  );
}

/** Hover crosshair: vertical line + a dot on each drawn curve at the
 * hovered level. */
function Crosshair({
  data,
  spot,
  bounds,
}: Readonly<{ data: PayoffView; spot: number; bounds: PayoffBounds }>): ReactElement {
  const x = xAt(spot, data.spots);
  return (
    <>
      <line
        x1={x}
        y1={0}
        x2={x}
        y2={PAYOFF_H}
        stroke="var(--ink, #111827)"
        strokeWidth={1}
        strokeDasharray="2 2"
        opacity={0.6}
      />
      {data.todayPnl !== null && (
        <circle
          cx={x}
          cy={yAt(pnlAt(data.spots, data.todayPnl, spot), bounds)}
          r={3.5}
          fill="var(--accent, #2563eb)"
        />
      )}
      <circle
        cx={x}
        cy={yAt(expiryPnlAt(data.legs, spot), bounds)}
        r={3.5}
        fill="var(--fill-success, #16a34a)"
      />
    </>
  );
}

/** Sensibull-style tooltip: the hovered level, its % move vs spot, and
 * the P&L on each curve there. Flips sides past mid-chart so it never
 * runs off the right edge. */
function HoverTip({
  data,
  spot,
  frac,
}: Readonly<{ data: PayoffView; spot: number; frac: number }>): ReactElement {
  const expiry = expiryPnlAt(data.legs, spot);
  const today = data.todayPnl === null ? null : pnlAt(data.spots, data.todayPnl, spot);
  const move =
    data.spot === null || data.spot === 0 ? null : ((spot - data.spot) / data.spot) * 100;
  const flip = frac > 0.55;
  return (
    <div
      data-testid="payoff-tooltip"
      className="pointer-events-none absolute top-2 z-10 min-w-40 rounded-lg border border-line bg-surface-0 p-2 text-xs shadow-lg"
      style={
        flip
          ? { right: `${String((1 - frac) * 100)}%`, marginRight: 10 }
          : { left: `${String(frac * 100)}%`, marginLeft: 10 }
      }
    >
      <div className="text-ink-muted">When price is at</div>
      <div className="mb-1 font-mono text-sm text-ink">
        {formatLevel(spot)}
        {move !== null && (
          <span className={cn("ml-1.5 text-[10px]", pnlTone(move))}>
            {move > 0 ? "+" : ""}
            {move.toFixed(2)}%
          </span>
        )}
      </div>
      {today !== null && <TipRow label="Today" value={today} />}
      <TipRow label="On expiry" value={expiry} />
    </div>
  );
}

function TipRow({ label, value }: Readonly<{ label: string; value: number }>): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      <span className={cn("font-mono", pnlTone(value))}>{formatMoney(value)}</span>
    </div>
  );
}

function ChartFooter({ data }: Readonly<{ data: PayoffView }>): ReactElement {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-ink-secondary">
      <LegendSwatch className="bg-success" label="On expiry" />
      {data.todayPnl !== null && <LegendSwatch className="bg-accent" label="Today" />}
      {data.spot !== null && (
        <span>
          Spot <span className="font-mono text-ink">{formatLevel(data.spot)}</span>
        </span>
      )}
    </div>
  );
}

function LegendSwatch({
  className,
  label,
}: Readonly<{ className: string; label: string }>): ReactElement {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn("inline-block h-0.5 w-4 rounded-full", className)} />
      {label}
    </span>
  );
}
